import {
  SEU_LANGUAGES,
  SeuConfig,
  SeuConfigApi,
  SeuConfigApiValue,
  SeuConfigFormDefinition,
  SeuConfigFormDefinitions,
  SeuConfigSvgDefinitions,
  SeuConfigTranslationObject,
  SeuConfigTranslationSelectOption,
  SeuLanguage,
  SeuSetupConverter,
} from '@seu-types';

function isFormDefinition(
  value: SeuConfigApiValue | undefined
): value is SeuConfigFormDefinition {
  return (
    typeof value === 'object' &&
    value !== null &&
    'items' in value &&
    Array.isArray((value as SeuConfigFormDefinition).items)
  );
}

function isString(value: SeuConfigApiValue | undefined): value is string {
  return typeof value === 'string';
}

function isTranslationObject(
  value: SeuConfigApiValue | undefined
): value is SeuConfigTranslationObject {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !('items' in value) &&
    !('unitOrder' in value)
  );
}

export const configConverter: SeuSetupConverter<SeuConfig, SeuConfigApi> = {
  fromApi: (input) => {
    const formDefinitions: SeuConfigFormDefinitions = {};
    const hierarchy: string[] = [];
    const svgDefinitions: SeuConfigSvgDefinitions = {};

    for (const fileName in input) {
      const value = (input as Record<string, SeuConfigApiValue | undefined>)[
        fileName
      ];

      if (
        fileName.includes('.json') &&
        fileName !== 'DE.json' &&
        fileName !== 'EN.json'
      ) {
        if (isFormDefinition(value)) {
          const key = fileName.replace('.json', '');
          formDefinitions[key] = value;
        }
      }

      if (fileName === 'hierarchy') {
        if (isString(value)) {
          const hierarchyItems = value.split('\n');
          hierarchyItems.forEach((hierarchyItem) => {
            hierarchy.push(hierarchyItem);
          });
        }
      }

      if (fileName.includes('.svg')) {
        if (isString(value)) {
          const key = fileName.replace('.svg', '');
          svgDefinitions[key] = value;
        }
      }
    }

    const deJson = input['DE.json'];
    const enJson = input['EN.json'];

    const translations: { [key in SeuLanguage]: SeuConfigTranslationObject } = {
      DE: isTranslationObject(deJson) ? deJson : {},
      EN: isTranslationObject(enJson) ? enJson : {},
    };

    // Process allUnits and allUnitKeywords for both languages
    SEU_LANGUAGES.forEach((lang) => {
      const allUnitsMap = new Map<string, string>();
      const allUnitKeywordsMap = new Map<string, string[]>();
      const allUnitGroups = translations[lang][
        'units'
      ] as SeuConfigTranslationObject;

      Object.keys(allUnitGroups).forEach((unitGroupKey) => {
        const allUnits = allUnitGroups[
          unitGroupKey
        ] as SeuConfigTranslationSelectOption[];

        allUnits.forEach((unit: SeuConfigTranslationSelectOption) => {
          const key = unit.pythonName;
          const label = unit.displayName;
          const keywords = unit.synonyms;
          allUnitsMap.set(key, label);
          allUnitKeywordsMap.set(key, keywords);
        });
      });

      translations[lang]['allUnits'] = Object.fromEntries(
        allUnitsMap.entries()
      );
      translations[lang]['allUnitKeywords'] = Object.fromEntries(
        allUnitKeywordsMap.entries()
      );
    });

    const config = {
      formDefinitions,
      svgs: svgDefinitions,
      translations,
      hierarchy,
      opexPresets: input['opexPresets.json'],
      limitsPresets: input['limitsPresets.json'],
      unitOrder: input['unitOrder.json']?.unitOrder,
    };

    return config;
  },
};
